import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { apiClient } from '../api';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import { useAssistantRecord } from '../contexts/ChatContext';
import { errMessage, secondaryBtn, secondaryBtnStyle } from '../components/UI/ink';
import { ago, parseTs } from '../components/SecurityAlerts/model';
import FindingDetail, { SevChip } from '../components/Findings/FindingDetail';
import NewCaseDrawer from '../components/Findings/NewCaseDrawer';
import { useVerdicts } from '../hooks/useVerdicts';
import VerdictBadge from '../components/Triage/VerdictBadge';
import {
  OPEN_STAGES, SEVS, SEV_LABEL, STAGES, STAGE_LABEL, clean, sevDotBorder, sevFg, sevKey, sevRank,
} from '../components/Findings/model';

// A triage pipeline: the lifecycle strip is the status filter, the list is
// sorted severity-then-newest, and the selected finding opens in a docked
// pane (same pattern as Security Alerts). Selection lives in ?finding=.
const seg = (on) => ({
  background: on ? 'var(--surf-raised)' : 'var(--surf-surface)',
  borderColor: on ? 'var(--accent)' : 'var(--surf-line)',
});
const selectStyle = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };

const normalise = (f) => ({
  ...f,
  severity: sevKey(f.severity),
  assigned_to: clean(f.assigned_to),
  notes: clean(f.notes),
  mitre_techniques: Array.isArray(f.mitre_techniques) ? f.mitre_techniques : [],
  correlated_alerts: Array.isArray(f.correlated_alerts) ? f.correlated_alerts : [],
});

const ageText = (iso) => {
  const d = parseTs(iso);
  if (!d) return '';
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h`;
  return `${Math.floor(m / 1440)} d`;
};

export default function Findings() {
  const { user, permissions = [] } = useAuth();
  const canRate = permissions.includes('events:update');
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState([]);
  const [resolvedList, setResolvedList] = useState(null);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState({});
  const [activeRules, setActiveRules] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [stage, setStage] = useState(null);
  const [sevs, setSevs] = useState([]);
  const [search, setSearch] = useState('');
  const [who, setWho] = useState('anyone');
  const [asset, setAsset] = useState('all');
  const [pinned, setPinned] = useState(null);
  const [caseFor, setCaseFor] = useState(null);
  const noticeTimer = useRef(null);
  const selectedId = searchParams.get('finding');
  // Includes the opened finding even when it is not in the loaded lists (deep link via ?finding=).
  const verdictKeys = useMemo(() => {
    const keys = [...open, ...(resolvedList || [])].map(f => `finding:${f.id}`);
    if (selectedId) keys.push(`finding:${selectedId}`);
    return keys;
  }, [open, resolvedList, selectedId]);
  const { verdicts, setVerdict } = useVerdicts(verdictKeys);

  // ?rule= comes from "All findings from this rule" on the Detection rules page.
  const ruleFilter = searchParams.get('rule');
  const clearRule = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('rule');
    setSearchParams(next, { replace: true });
  };
  const select = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('finding', id); else next.delete('finding');
    setSearchParams(next, { replace: true });
  };

  const flash = (kind, text) => {
    setNotice({ kind, text });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 5000);
  };
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const load = useCallback(async () => {
    setLoading(true);
    const [o, s] = await Promise.allSettled([
      apiClient.get(`/api/findings?status=${OPEN_STAGES.join(',')}&limit=200`),
      apiClient.get('/api/findings/stats'),
    ]);
    if (o.status === 'fulfilled') setOpen((o.value.data?.findings || []).map(normalise));
    else flash('error', errMessage(o.reason, 'Could not load findings.'));
    if (s.status === 'fulfilled') setStats(s.value.data?.stats || null);
    if (stage === 'resolved') {
      try {
        const r = await apiClient.get('/api/findings?status=resolved&limit=200');
        setResolvedList((r.data?.findings || []).map(normalise));
      } catch { setResolvedList([]); }
    }
    setLoading(false);
  }, [stage]);

  useEffect(() => { load(); }, [load]);

  // Rule names and assignable members are loaded once; both are optional.
  useEffect(() => {
    apiClient.get('/api/rules?limit=200')
      .then(res => {
        const list = res.data?.rules || [];
        setRules(Object.fromEntries(list.map(r => [r.id, r])));
        setActiveRules(list.filter(r => r.enabled === 1 || r.enabled === true).length);
      })
      .catch(() => {});
    orgApi.getMembers()
      .then(m => setMembers((m?.members || m?.users || []).filter(x => x.email && (!x.status || x.status === 'active'))))
      .catch(() => {});
  }, []);

  const myEmail = user?.email || user?.sub || '';
  const me = { email: myEmail, name: members.find(m => m.email === myEmail)?.full_name || user?.name || null };
  const nameOf = (email) => (email ? members.find(m => m.email === email)?.full_name || email : null);

  const scope = stage === 'resolved' ? (resolvedList || []) : stage ? open.filter(f => f.status === stage) : open;
  const hosts = useMemo(() => [...new Set(scope.map(f => f.server_hostname || f.server_id).filter(Boolean))].sort(), [scope]);

  const q = search.trim().toLowerCase();
  const matchesBase = (f) => {
    if (ruleFilter && f.rule_id !== ruleFilter) return false;
    if (who === 'me' && f.assigned_to !== myEmail) return false;
    if (who === 'none' && f.assigned_to) return false;
    if (asset !== 'all' && (f.server_hostname || f.server_id) !== asset) return false;
    if (!q) return true;
    const rule = rules[f.rule_id];
    return [f.title, f.description, f.server_hostname, f.rule_id, rule?.name, f.evidence?.entity, ...f.mitre_techniques]
      .some(v => v && String(v).toLowerCase().includes(q));
  };
  const base = scope.filter(matchesBase);
  const sevCounts = {};
  base.forEach(f => { sevCounts[f.severity] = (sevCounts[f.severity] || 0) + 1; });
  const visible = base
    .filter(f => !sevs.length || sevs.includes(f.severity))
    .sort((a, b) => sevRank(a.severity) - sevRank(b.severity) || (parseTs(b.detected_at)?.getTime() || 0) - (parseTs(a.detected_at)?.getTime() || 0));

  const inList = [...open, ...(resolvedList || [])].find(f => f.id === selectedId);
  const selected = inList || (pinned?.id === selectedId ? pinned : null);
  useEffect(() => { if (inList) setPinned(inList); }, [inList]);
  useAssistantRecord(selected && { kind: 'finding', id: selected.id, label: selected.id });

  // Stage strip: counts come from stats (all findings), hints from open ones.
  const byStatus = stats?.byStatus || {};
  const openBy = (st) => open.filter(f => f.status === st);
  const oldestNew = openBy('new').reduce((min, f) => {
    const t = parseTs(f.detected_at)?.getTime();
    return t && (!min || t < min) ? t : min;
  }, null);
  const owners = new Set(openBy('investigating').map(f => f.assigned_to).filter(Boolean)).size;
  const unassignedTriaged = openBy('triaged').filter(f => !f.assigned_to).length;
  const stageSub = {
    new: oldestNew ? `oldest ${ageText(new Date(oldestNew).toISOString())}` : 'nothing waiting',
    triaged: openBy('triaged').length ? (unassignedTriaged ? `${unassignedTriaged} unassigned` : 'all assigned') : 'none',
    investigating: `${owners} owner${owners === 1 ? '' : 's'}`,
    contained: openBy('contained').length ? 'ready to close' : 'none',
    resolved: 'all time',
  };

  const openCount = open.length;
  const critOpen = open.filter(f => f.severity === 'critical').length;
  const highOpen = open.filter(f => f.severity === 'high').length;
  const summary = loading && !stats
    ? 'Loading…'
    : [
        `${openCount} open`,
        `${critOpen} critical`,
        `${highOpen} high`,
        activeRules != null && `raised by ${activeRules} active detection rule${activeRules === 1 ? '' : 's'}`,
      ].filter(Boolean).join(' · ');

  const update = async (patch) => {
    if (!selected) return false;
    setBusy(true);
    try {
      await apiClient.put(`/api/findings/${selected.id}`, patch);
      const now = new Date().toISOString();
      const stamped = { ...selected, ...patch };
      if (patch.status && patch.status !== selected.status) {
        if (patch.status === 'triaged') stamped.triaged_at = now;
        if (patch.status === 'contained') stamped.contained_at = now;
        if (patch.status === 'resolved') stamped.resolved_at = now;
      }
      setPinned(stamped);
      const swap = (list) => list.map(f => (f.id === stamped.id ? stamped : f));
      setOpen(list => (OPEN_STAGES.includes(stamped.status)
        ? (list.some(f => f.id === stamped.id) ? swap(list) : [stamped, ...list])
        : list.filter(f => f.id !== stamped.id)));
      setResolvedList(list => (list ? (stamped.status === 'resolved' ? (list.some(f => f.id === stamped.id) ? swap(list) : [stamped, ...list]) : list.filter(f => f.id !== stamped.id)) : list));
      if (patch.status) {
        flash('ok', `Moved to ${STAGE_LABEL[patch.status]}.`);
        apiClient.get('/api/findings/stats').then(s => setStats(s.data?.stats || null)).catch(() => {});
      }
      return true;
    } catch (err) {
      flash('error', errMessage(err, 'Could not update the finding.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const listLabel = `${visible.length} ${stage ? STAGE_LABEL[stage].toUpperCase() : 'OPEN'} FINDING${visible.length === 1 ? '' : 'S'}`;

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-4" style={{ color: 'var(--ink-text)' }}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>INTELLIGENCE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Findings</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <Link to="/rules" className={secondaryBtn} style={secondaryBtnStyle}>
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.8} />Detection rules
          </Link>
          <button type="button" onClick={load} disabled={loading} aria-label="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border disabled:opacity-60" style={secondaryBtnStyle}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={notice.kind === 'error'
          ? { background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' }
          : { background: 'var(--sev-good-bg)', borderColor: 'var(--sev-good-fg)', color: 'var(--ink-text)' }}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Lifecycle strip, doubles as the status filter */}
      <div role="group" aria-label="Lifecycle stage" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {STAGES.map((st, i) => {
          const on = stage === st;
          return (
            <button key={st} type="button" onClick={() => { setStage(on ? null : st); setSevs([]); }} aria-pressed={on} className="h-[70px] box-border flex flex-col justify-center gap-1 px-4 rounded-xl border text-left" style={seg(on)}>
              <span className="flex items-center justify-between">
                <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{STAGE_LABEL[st].toUpperCase()}</span>
                {i < STAGES.length - 1 && <ChevronRight className="w-3.5 h-3.5 hidden lg:block" strokeWidth={2} style={{ color: 'var(--surf-line-strong)' }} />}
              </span>
              <span className="flex items-baseline gap-2 min-w-0">
                <span className="font-heading text-2xl font-semibold leading-none">{stats ? byStatus[st] || 0 : '—'}</span>
                <span className="text-xs truncate" style={{ color: 'var(--ink-faint)' }}>{stageSub[st]}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {ruleFilter && (
          <button type="button" onClick={clearRule} className="h-10 flex items-center gap-2 px-3 rounded-[10px] border text-[13px]" style={{ background: 'var(--surf-raised)', borderColor: 'var(--accent)', color: 'var(--ink-text)' }}>
            Rule: {rules[ruleFilter]?.name || ruleFilter}<X className="w-3.5 h-3.5" aria-label="Clear rule filter" />
          </button>
        )}
        <div className="w-60 h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-[15px] h-[15px] shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
          <input type="search" aria-label="Search findings" placeholder="Title, host, rule or T-id" value={search} onChange={e => setSearch(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        <div role="group" aria-label="Severity" className="flex flex-wrap gap-1.5">
          {SEVS.map(k => {
            const on = sevs.includes(k);
            return (
              <button key={k} type="button" onClick={() => setSevs(p => (on ? p.filter(x => x !== k) : [...p, k]))} aria-pressed={on} className="h-10 flex items-center gap-[7px] px-[11px] rounded-[10px] border text-[13px]" style={seg(on)}>
                <span className="w-2 h-2 box-border rounded-sm" style={{ background: sevFg(k), border: `1px solid ${sevDotBorder(k)}` }} />
                {SEV_LABEL[k]}
                <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{sevCounts[k] || 0}</span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 xl:ml-auto">
          <label htmlFor="f-who" className="sr-only">Assignee</label>
          <select id="f-who" value={who} onChange={e => setWho(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
            <option value="anyone">Anyone</option>
            <option value="me">Assigned to me</option>
            <option value="none">Unassigned</option>
          </select>
          <label htmlFor="f-asset" className="sr-only">Asset</label>
          <select id="f-asset" value={asset} onChange={e => setAsset(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px] max-w-[200px]" style={selectStyle}>
            <option value="all">All assets</option>
            {hosts.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_480px] gap-5 xl:h-[calc(100vh-390px)] xl:min-h-[560px]">
        <section aria-label="Findings" className="min-h-[520px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <div className="h-[42px] box-border flex items-center px-4 border-b shrink-0" style={{ borderColor: 'var(--surf-line)' }}>
            <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{listLabel}</span>
            <span className="ml-auto text-xs hidden sm:inline" style={{ color: 'var(--ink-faint)' }}>Severity, then newest</span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {visible.length === 0 && (
              <div className="py-12 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
                {loading ? 'Loading findings…'
                  : scope.length ? 'No findings match these filters.'
                  : stage ? `Nothing ${STAGE_LABEL[stage].toLowerCase()}.`
                  : 'No open findings. They appear here when a detection rule fires on an agent snapshot.'}
              </div>
            )}
            {visible.map(f => {
              const on = f.id === selectedId;
              const idx = STAGES.indexOf(f.status);
              const rule = rules[f.rule_id];
              const meta = [`${f.server_hostname || f.server_id}${Number(f.asset_deleted) ? ' (asset deleted)' : ''}`, rule?.name || f.rule_id, nameOf(f.assigned_to)].filter(Boolean).join(' · ');
              return (
                <div key={f.id} className="relative">
                  <button type="button" onClick={() => select(on ? null : f.id)} aria-pressed={on} className="w-full h-16 box-border flex items-center gap-3 px-4 border-b text-left" style={{ borderColor: 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'transparent' }}>
                    <span className="w-[84px] shrink-0"><SevChip severity={f.severity} /></span>
                    <span className="flex-grow min-w-0 flex flex-col gap-1">
                      <span className="text-sm font-medium truncate">{f.title}</span>
                      <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{meta}</span>
                    </span>
                    <VerdictBadge verdict={verdicts[`finding:${f.id}`]} />
                    <span className="w-[108px] shrink-0 hidden sm:flex flex-col gap-[5px]">
                      <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>{STAGE_LABEL[f.status] || f.status}</span>
                      <span className="flex gap-0.5" aria-hidden="true">
                        {STAGES.map((st, i) => (
                          <span key={st} className="w-[18px] h-[3px] rounded-sm" style={{ background: i < idx ? 'var(--ink-muted)' : i === idx ? 'var(--ink-text)' : 'var(--surf-line)' }} />
                        ))}
                      </span>
                    </span>
                    <span className="w-12 shrink-0 text-right font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{ago(f.detected_at)}</span>
                  </button>
                  {on && <span aria-hidden="true" className="absolute right-0 top-5 w-px h-6" style={{ background: 'var(--accent)' }} />}
                </div>
              );
            })}
          </div>
        </section>

        <FindingDetail
          finding={selected}
          rule={selected ? rules[selected.rule_id || selected.evidence?.rule_id] : null}
          members={members}
          me={me}
          busy={busy}
          onClose={() => select(null)}
          onUpdate={update}
          onNewCase={() => selected && setCaseFor(selected)}
          onNotice={(text) => flash('ok', text)}
          verdict={selected ? verdicts[`finding:${selected.id}`] : undefined}
          canRate={canRate}
          onVerdictChange={(v) => selected && setVerdict(`finding:${selected.id}`, v)}
        />
      </div>

      <NewCaseDrawer finding={caseFor} onClose={() => setCaseFor(null)} />
    </div>
  );
}
